import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Store, Loader2, CheckCircle2, Clock, XCircle, Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  { value: "business_license", label: "Business license" },
  { value: "ein_letter", label: "EIN / Tax ID letter" },
  { value: "id_front", label: "Government ID (front)" },
  { value: "id_back", label: "Government ID (back)" },
  { value: "address_proof", label: "Proof of address" },
  { value: "other", label: "Other supporting document" },
];

const ACCEPT = "image/png,image/jpeg,image/webp,application/pdf";
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

interface UploadedDoc {
  path: string;
  name: string;
  type: string;
  size: number;
  hash: string;
}

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const schema = z.object({
  store_name: z.string().trim().min(2, "Store name required").max(80),
  category: z.string().trim().min(2, "Category required").max(40),
  description: z.string().trim().max(500).optional().or(z.literal("")),
  website: z.string().trim().url("Invalid URL").max(200).optional().or(z.literal("")),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  country: z.string().trim().min(2).max(2),
});

interface Application {
  id: string;
  status: string;
  store_name: string;
  category: string;
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
}

const CATEGORIES = [
  "Food & Drink", "Retail", "Services", "Beauty", "Fitness",
  "Entertainment", "Creator", "Education", "Other",
];

const MerchantOnboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [app, setApp] = useState<Application | null>(null);
  const [isMerchant, setIsMerchant] = useState(false);
  const [form, setForm] = useState({
    store_name: "", category: "Food & Drink", description: "",
    website: "", phone: "", country: "US",
  });
  const [docs, setDocs] = useState<UploadedDoc[]>([]);
  const [docType, setDocType] = useState("business_license");
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const [{ data: profile }, { data: apps }] = await Promise.all([
      supabase.from("profiles").select("is_merchant").eq("user_id", user.id).maybeSingle(),
      supabase.from("merchant_applications" as any)
        .select("id,status,store_name,category,admin_notes,created_at,reviewed_at")
        .eq("user_id", user.id).order("created_at", { ascending: false }).limit(1),
    ]);
    setIsMerchant(!!(profile as any)?.is_merchant);
    setApp(((apps as any) ?? [])[0] ?? null);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;
    if (file.size > MAX_BYTES) { toast.error("File too large (max 10 MB)"); return; }
    if (!ACCEPT.split(",").includes(file.type)) { toast.error("Unsupported file type"); return; }
    if (docs.length >= 10) { toast.error("Maximum 10 documents"); return; }

    if (docs.some((d) => d.name.toLowerCase() === file.name.toLowerCase() && d.size === file.size)) {
      toast.error("This file is already uploaded");
      return;
    }

    setUploading(true);
    const hash = await sha256Hex(file);
    if (docs.some((d) => d.hash === hash)) {
      setUploading(false);
      toast.error("Duplicate file detected");
      return;
    }

    const ext = file.name.split(".").pop() || "bin";
    const path = `${user.id}/${Date.now()}-${docType}.${ext}`;
    const { error } = await supabase.storage.from("merchant-docs").upload(path, file, {
      contentType: file.type, upsert: false,
    });
    setUploading(false);
    if (error) { toast.error(error.message); return; }
    setDocs((d) => [...d, { path, name: file.name, type: docType, size: file.size, hash }]);
    toast.success("Uploaded");
  };

  const removeDoc = async (d: UploadedDoc) => {
    await supabase.storage.from("merchant-docs").remove([d.path]);
    setDocs((arr) => arr.filter((x) => x.path !== d.path));
  };

  const submit = async () => {
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0].message);
      return;
    }
    if (docs.length === 0) {
      toast.error("Please upload at least one supporting document");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.rpc("submit_merchant_application" as any, {
      _store_name: parsed.data.store_name,
      _category: parsed.data.category,
      _description: parsed.data.description || null,
      _website: parsed.data.website || null,
      _phone: parsed.data.phone || null,
      _country: parsed.data.country,
      _documents: docs as any,
    });
    setSubmitting(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Application submitted for review");
    load();
  };

  return (
    <MobileShell>
      <BrandHeader>
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-medium">
            <Store className="h-3.5 w-3.5" /> Merchant onboarding
          </div>
          <h1 className="mt-3 font-display text-2xl font-bold">Become a merchant</h1>
          <p className="text-xs opacity-80 mt-1">Accept payments at your business</p>
        </div>
      </BrandHeader>

      <section className="px-5 -mt-4 pb-8 space-y-4">
        {loading ? (
          <div className="py-12 grid place-items-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : isMerchant ? (
          <Card className="p-5 text-center space-y-3">
            <CheckCircle2 className="h-10 w-10 text-success mx-auto" />
            <div>
              <p className="font-display text-lg font-bold">You're a merchant</p>
              <p className="text-sm text-muted-foreground">Open your merchant dashboard.</p>
            </div>
            <Button className="w-full" onClick={() => navigate("/merchant")}>Go to merchant</Button>
          </Card>
        ) : app && app.status === "pending" ? (
          <Card className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              <p className="font-display font-bold">Under review</p>
              <Badge variant="secondary" className="ml-auto">Pending</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Your application for <strong>{app.store_name}</strong> ({app.category}) was submitted{" "}
              {new Date(app.created_at).toLocaleDateString()}. We'll notify you when it's reviewed.
            </p>
          </Card>
        ) : (
          <>
            {app?.status === "rejected" && (
              <Card className="p-4 border-destructive/50 bg-destructive/5">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle className="h-4 w-4 text-destructive" />
                  <p className="text-sm font-medium">Previous application rejected</p>
                </div>
                {app.admin_notes && (
                  <p className="text-xs text-muted-foreground">Reason: {app.admin_notes}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">You may submit a new application below.</p>
              </Card>
            )}

            <Card className="p-5 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="store_name">Store name *</Label>
                <Input id="store_name" value={form.store_name} maxLength={80}
                  onChange={(e) => setForm({ ...form, store_name: e.target.value })}
                  placeholder="Sunny's Tacos" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <select id="category" value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" value={form.description} maxLength={500}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What does your business do?" rows={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input id="website" type="url" value={form.website} maxLength={200}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://example.com" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" value={form.phone} maxLength={30}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1 555 …" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input id="country" value={form.country} maxLength={2}
                    onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
                    placeholder="US" />
                </div>
              </div>

              {/* Documents */}
              <div className="space-y-2 pt-2 border-t border-border">
                <Label>Supporting documents *</Label>
                <p className="text-[11px] text-muted-foreground">
                  Upload at least one document (business license, ID, EIN, proof of address). PDF / JPG / PNG / WebP, max 10 MB each.
                </p>
                <div className="flex gap-2">
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    className="flex h-10 flex-1 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                  <label className="inline-flex items-center gap-1 h-10 px-3 rounded-md border border-input bg-background text-sm cursor-pointer hover:bg-muted">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    <span>Add</span>
                    <input type="file" accept={ACCEPT} className="hidden" onChange={handleFile} disabled={uploading} />
                  </label>
                </div>
                {docs.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {docs.map((d) => {
                      const label = DOC_TYPES.find((t) => t.value === d.type)?.label ?? d.type;
                      return (
                        <li key={d.path} className="flex items-center gap-2 text-xs bg-muted/50 rounded-md px-2 py-1.5">
                          <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{label}</div>
                            <div className="truncate text-muted-foreground">{d.name} · {(d.size / 1024).toFixed(0)} KB</div>
                          </div>
                          <button type="button" onClick={() => removeDoc(d)}
                            className="h-6 w-6 grid place-items-center rounded-md hover:bg-destructive/10 text-destructive">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {docs.length === 0 && (
                <p className="text-[11px] text-destructive text-center -mb-1">
                  Please upload at least one supporting document to submit.
                </p>
              )}
              <Button className="w-full" onClick={submit} disabled={submitting || uploading || docs.length === 0}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                Submitting confirms the information is accurate. False details may result in account suspension.
              </p>
            </Card>
          </>
        )}
      </section>
    </MobileShell>
  );
};

export default MerchantOnboarding;
